/**
 * Helpers for the chat panel's task-timing window: the log-scale minute
 * stops both sliders snap to, and the "×2.3 faster · saved 12m" readout.
 */

/** Slider stops. Log-ish spacing: fine at the short end, coarse past an hour. */
export const MINUTE_STOPS = [
  1, 2, 3, 5, 10, 15, 20, 30, 45, 60, 90, 120, 180, 240,
] as const;

export type MinuteStop = (typeof MINUTE_STOPS)[number];

/** Longest a typed estimate may be; matches the create input's cap. */
export const MAX_MINUTES = 24 * 60;

/**
 * Index of the stop closest to `minutes`, so a typed exact value still has
 * a slider position. Ties go to the shorter stop.
 */
export function nearestStopIndex(minutes: number): number {
  let best = 0;

  for (let i = 1; i < MINUTE_STOPS.length; i += 1) {
    if (
      Math.abs(MINUTE_STOPS[i] - minutes) <
      Math.abs(MINUTE_STOPS[best] - minutes)
    ) {
      best = i;
    }
  }

  return best;
}

/**
 * Whole minutes from what the operator typed, or null when the text is not
 * a usable duration. Clamped to MAX_MINUTES; fractions round.
 */
export function parseTypedMinutes(text: string): number | null {
  const value = Number(text.trim());

  if (!Number.isFinite(value) || value < 0.5) return null;

  return Math.min(MAX_MINUTES, Math.round(value));
}

export function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)}m`;

  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);

  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

export type SpeedupVerdict = 'faster' | 'same' | 'slower';

export interface SpeedupSummary {
  verdict: SpeedupVerdict;
  /** minutesByHand / minutesWithAgent. Above 1 means the agent was faster. */
  ratio: number;
  /** One line for the UI, e.g. "×2.3 faster · saved 12m". */
  label: string;
}

/** Ratios within this band of 1 read as "about the same". */
const SAME_BAND = 0.1;

export function describeSpeedup(
  minutesByHand: number,
  minutesWithAgent: number,
): SpeedupSummary {
  const ratio = minutesByHand / minutesWithAgent;

  if (Math.abs(ratio - 1) < SAME_BAND) {
    return { verdict: 'same', ratio, label: 'about the same' };
  }

  if (ratio > 1) {
    return {
      verdict: 'faster',
      ratio,
      label: `×${ratio.toFixed(1)} faster · saved ${formatMinutes(
        minutesByHand - minutesWithAgent,
      )}`,
    };
  }

  return {
    verdict: 'slower',
    ratio,
    label: `${ratio.toFixed(1)}× slower · cost ${formatMinutes(
      minutesWithAgent - minutesByHand,
    )}`,
  };
}

/** Conversation elapsed time is only a suggestion; the operator confirms the estimate. */
export function estimateConversationMinutes(
  start: string | undefined,
  end: string | undefined,
): number | null {
  if (!start || !end) return null;

  const elapsed = Date.parse(end) - Date.parse(start);

  if (!Number.isFinite(elapsed) || elapsed < 0) return null;

  return Math.min(MAX_MINUTES, Math.max(1, Math.round(elapsed / 60_000)));
}
